import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getOrgSSOStatus,
  configureOrgSSO,
  initializeOrgSSO,
  disconnectOrgSSO,
} from '@kirin/api-client/api/sso-settings';
import type { ssoSettings } from '@kirin/api-client/contracts';

/**
 * Fetch SSO status and SP credentials for an organization.
 * Returns ACS URL, Entity ID, Tenant ID, and configuration state.
 */
export function useOrgSSOStatus(organizationId: string) {
  return useQuery({
    queryKey: queryKeys.ssoSettings.organization(organizationId),
    queryFn: () => getOrgSSOStatus(apiClient, organizationId),
    enabled: !!organizationId,
    retry: retryHandlers.detail,
  });
}

/**
 * Initialize an empty SSO slot so the ssoId is available before the customer
 * has their IdP metadata URL. Idempotent — safe to call even if already initialized.
 * On success, updates the SSO status cache.
 */
export function useInitializeOrgSSO(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => initializeOrgSSO(apiClient, organizationId),
    onSuccess: (data: ssoSettings.SSOStatusResponse) => {
      queryClient.setQueryData(
        queryKeys.ssoSettings.organization(organizationId),
        data
      );
    },
  });
}

/**
 * Configure SAML SSO for an organization by submitting the IdP Metadata URL.
 * On success, invalidates the SSO status cache.
 */
export function useConfigureOrgSSO(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ssoSettings.ConfigureSSOBody) =>
      configureOrgSSO(apiClient, organizationId, body),
    onSuccess: (data: ssoSettings.SSOStatusResponse) => {
      queryClient.setQueryData(
        queryKeys.ssoSettings.organization(organizationId),
        data
      );
    },
  });
}

/**
 * Disconnect SSO configuration for an organization.
 * Deletes the SSO slot in Descope and releases the email domain.
 * On success, invalidates the SSO status cache.
 */
export function useDisconnectOrgSSO(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => disconnectOrgSSO(apiClient, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ssoSettings.organization(organizationId),
      });
    },
  });
}
